// Step 22 — DealActions filtered to this buyer.
//
// Reuses `listDealActions(workspaceId, {acquirer_id})` — no new
// endpoint. Links out to the existing human-controlled actions page.
// No approve/reject/start/complete/cancel/supersede path is exposed
// here (or anywhere in the AI portal — those verbs are broker-only).

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { listDealActions } from "../../api";
import { AIEmpty, AIError, AILoading, AISection } from "../AIPrimitives";

const STATUS_LABEL = {
  proposed: "Föreslagen",
  pending_review: "Under granskning",
  approved: "Godkänd",
  in_progress: "Pågår",
  blocked: "Blockerad",
  completed: "Klar",
  rejected: "Avvisad",
  cancelled: "Avbruten",
  superseded: "Ersatt",
};

export default function AIBuyerActions({ acquirerId, workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!workspaceId || !acquirerId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await listDealActions(
          workspaceId,
          { acquirer_id: acquirerId, limit: 50 },
          { signal },
        );
        setItems(res?.results || []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda åtgärder.");
      } finally {
        setLoading(false);
      }
    },
    [workspaceId, acquirerId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  return (
    <AISection
      title="Åtgärder kopplade till köparen"
      right={
        <Link
          to={`/deals/${workspaceId}/actions`}
          className="qai-page-section-link"
        >
          Alla åtgärder →
        </Link>
      }
    >
      <AIError message={error} />
      {loading && items.length === 0 ? (
        <AILoading />
      ) : items.length === 0 ? (
        <AIEmpty
          title="Inga åtgärder kopplade till denna köpare"
          hint={
            "Nya åtgärder kan föreslås av AI:n via `propose_deal_action`. " +
            "Godkännande sker manuellt i granskningsvyn."
          }
        />
      ) : (
        <ul className="qai-buyer-actions-list">
          {items.map((a) => (
            <li key={a.id} className="qai-buyer-action-row">
              <div className="qai-buyer-action-main">
                <div className="qai-buyer-action-title">{a.title}</div>
                <div className="qai-buyer-action-meta">
                  <span className="qai-buyer-action-status">
                    {STATUS_LABEL[a.status] || a.status}
                  </span>
                  {a.priority && (
                    <span className="qai-buyer-action-prio">
                      · Prioritet: {a.priority}
                    </span>
                  )}
                  {a.source_kind && (
                    <span className="qai-buyer-action-source">
                      · Källa: {a.source_kind}
                    </span>
                  )}
                </div>
              </div>
              <Link
                to={`/deals/${workspaceId}/actions`}
                className="qai-page-section-link"
              >
                Öppna →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AISection>
  );
}
